import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, useTheme, CircularProgress, Chip, Stack,
  IconButton, Tooltip as MuiTooltip
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Paleta de 10 cores distintas para os contratos
const COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

const HistoricoPerformanceChart = ({
  data = [],
  dataKey,
  title,
  yAxisLabel = '',
  loading = false,
  height = 350,
  onClick = null,
  // Props para sincronização entre gráficos
  hiddenContratos: externalHiddenContratos = null,
  onToggleContrato = null,
  // Mapa de cores externo para consistência entre gráficos
  colorMap: externalColorMap = null
}) => {
  const theme = useTheme();
  // Usa estado interno se não receber props externas (compatibilidade)
  const [internalHiddenContratos, setInternalHiddenContratos] = useState({});
  const hiddenContratos = externalHiddenContratos !== null ? externalHiddenContratos : internalHiddenContratos;

  // Processar dados para formato do Recharts
  // Transforma de [{mes, mes_label, des_contrato, valor}, ...]
  // para [{mes_label, contrato1: valor1, contrato2: valor2, ...}, ...]
  const { chartData, contratos, colorMap } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], contratos: [], colorMap: externalColorMap || {} };
    }

    // Extrair lista unica de contratos
    const contratosSet = new Set();
    data.forEach(item => {
      if (item.des_contrato) {
        contratosSet.add(item.des_contrato);
      }
    });
    const contratosArray = Array.from(contratosSet).sort();

    // Usar colorMap externo se disponível, senão criar local (fallback)
    let colors = externalColorMap;
    if (!colors || Object.keys(colors).length === 0) {
      colors = {};
      contratosArray.forEach((contrato, index) => {
        colors[contrato] = COLORS[index % COLORS.length];
      });
    }

    // Agrupar dados por mes
    const mesMap = {};
    data.forEach(item => {
      const mesLabel = item.mes_label || item.mes;
      if (!mesMap[mesLabel]) {
        mesMap[mesLabel] = { mes_label: mesLabel, mes: item.mes };
      }
      if (item.des_contrato && item[dataKey] !== undefined) {
        mesMap[mesLabel][item.des_contrato] = Number(item[dataKey]) || 0;
      }
    });

    // Ordenar por data (mes)
    const sortedData = Object.values(mesMap).sort((a, b) => {
      if (a.mes && b.mes) {
        return new Date(a.mes) - new Date(b.mes);
      }
      return 0;
    });

    return {
      chartData: sortedData,
      contratos: contratosArray,
      colorMap: colors
    };
  }, [data, dataKey, externalColorMap]);

  // Toggle visibilidade do contrato na legenda
  const handleLegendClick = (contrato) => {
    if (onToggleContrato) {
      // Usa callback externo para sincronizar entre gráficos
      onToggleContrato(contrato);
    } else {
      // Fallback para estado interno
      setInternalHiddenContratos(prev => ({
        ...prev,
        [contrato]: !prev[contrato]
      }));
    }
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Ordenar do maior para o menor valor
      const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));

      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            boxShadow: theme.shadows[4],
            maxHeight: 400,
            overflowY: 'auto'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.text.primary }}>
            {label}
          </Typography>
          {sortedPayload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  flexShrink: 0
                }}
              />
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '0.95rem' }}>
                {entry.name}: <strong style={{ color: theme.palette.text.primary }}>{entry.value?.toFixed(2)}{yAxisLabel}</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Legenda customizada clicavel
  const renderLegend = () => {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        gap={1}
        sx={{ mt: 2, px: 2 }}
      >
        {contratos.map((contrato) => (
          <Chip
            key={contrato}
            label={contrato}
            size="medium"
            onClick={() => handleLegendClick(contrato)}
            sx={{
              backgroundColor: hiddenContratos[contrato] ? 'grey.300' : colorMap[contrato],
              color: hiddenContratos[contrato] ? 'text.secondary' : 'white',
              opacity: hiddenContratos[contrato] ? 0.5 : 1,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500,
              height: 32,
              px: 0.5,
              '&:hover': {
                opacity: 0.85,
                transform: 'scale(1.02)',
                boxShadow: 1
              }
            }}
          />
        ))}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
            <Typography color="text.secondary">Sem dados para exibir</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'box-shadow 0.2s',
        '&:hover': onClick ? {
          boxShadow: theme.shadows[6]
        } : {}
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {onClick && (
            <MuiTooltip title="Ampliar gráfico">
              <IconButton
                size="small"
                onClick={onClick}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
        </Box>
        <Box sx={{ height: height, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 25, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="mes_label"
                tick={{ fill: theme.palette.text.secondary, fontSize: 13 }}
                stroke={theme.palette.divider}
                height={35}
              />
              <YAxis
                tick={{ fill: theme.palette.text.secondary, fontSize: 13 }}
                stroke={theme.palette.divider}
                width={55}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: theme.palette.text.secondary, fontSize: 14 }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={() => null} />

              {contratos.map((contrato) => (
                <Line
                  key={contrato}
                  type="monotone"
                  dataKey={contrato}
                  name={contrato}
                  stroke={colorMap[contrato]}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 7 }}
                  hide={hiddenContratos[contrato]}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        {renderLegend()}
      </CardContent>
    </Card>
  );
};

export default HistoricoPerformanceChart;
